import { useState } from 'react';
import { useApp } from '../context/useApp';
import { useLanguage } from '../i18n';
import { buildInterviewBrief } from '../prompts/interviewBrief';

interface AdvanceLauncherProps {
  projectId: string;
  onCancel: () => void;
  onStarted: (sessionId: string) => void;
}

export default function AdvanceLauncher({ projectId, onCancel, onStarted }: AdvanceLauncherProps) {
  const { t, locale } = useLanguage();
  const i = t.interview;
  const { projects } = useApp();
  const project = projects.find((p) => p.id === projectId);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (!project) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <p className="text-slate">No project selected.</p>
      </div>
    );
  }

  const startAdvance = async () => {
    setBusy(true);
    setErr('');
    try {
      const sectionBlob = Object.entries(project.sections || {})
        .map(([k, v]: any) => `## ${k}\n\n${v?.content || ''}`)
        .join('\n\n');
      const seed = {
        name: project.name,
        idea: project.input?.idea || '',
        industry: project.input?.industry,
        location: project.input?.location,
        teamSize: project.input?.teamSize,
        stage: project.input?.stage,
        goals: project.input?.goals,
        startingMaterial: sectionBlob,
      };
      const brief = buildInterviewBrief({ kind: 'project', seed, locale: project.input?.locale || locale });
      const response = await fetch('/api/interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          op: 'start',
          payload: { kind: 'project', seed, projectId: project.id, locale: project.input?.locale || locale, brief },
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setErr(data.error || 'Failed to start');
        return;
      }
      if (data.session?.id) onStarted(data.session.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to start');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="max-w-xl w-full bg-white border border-cloud p-8 text-center">
        <h2 className="text-2xl font-bold text-ink mb-2">{i.advanceTitle}</h2>
        <p className="text-slate mb-6">{i.advanceSubtitle}</p>
        {err && <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm border border-red-100">{err}</div>}
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-ink hover:bg-mist font-medium transition-colors"
          >
            {t.project.back}
          </button>
          <button
            type="button"
            onClick={startAdvance}
            disabled={busy}
            className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-medium transition-colors disabled:opacity-50"
          >
            {busy ? '...' : i.advanceStart}
          </button>
        </div>
      </div>
    </div>
  );
}
